/**
 * On-screen debug console log store.
 *
 * Holds a bounded, in-memory list of analytics/omnitrack actions performed
 * by the app and notifies subscribers when new entries are logged. This
 * module has no DOM dependency on purpose: the actual on-screen rendering
 * lives in src/components (this repo's DOM-specific UI layer), while `log()`
 * itself can be called from anywhere in `shared`.
 */

enum DebugConsoleCategory {
  ANALYTICS = 'ANALYTICS',
  OMNITRACK = 'OMNITRACK',
}

interface DebugConsoleEntry {
  id: number;
  timestamp: number;
  category: DebugConsoleCategory;
  action: string;
  payload?: unknown;
}

type DebugConsoleListener = (entries: DebugConsoleEntry[]) => void;

/** Caps memory usage; oldest entries are dropped once the limit is reached. */
const MAX_ENTRIES = 300;

class DebugConsoleManager {
  private static instance: DebugConsoleManager;
  private entries: DebugConsoleEntry[] = [];
  private listeners: Set<DebugConsoleListener> = new Set();
  private nextId = 0;

  static getInstance(): DebugConsoleManager {
    if (!DebugConsoleManager.instance) {
      DebugConsoleManager.instance = new DebugConsoleManager();
    }
    return DebugConsoleManager.instance;
  }

  /**
   * Log an analytics or omnitrack action so it shows up in the on-screen debug console.
   *
   * @param {DebugConsoleCategory} category - Whether this is an analytics or omnitrack action
   * @param {string} action - Short label identifying the action (e.g. method name)
   * @param {unknown} [payload] - Optional extra data to display alongside the action
   */
  log = (
    category: DebugConsoleCategory,
    action: string,
    payload?: unknown
  ): void => {
    const nextEntries = [
      ...this.entries,
      {
        id: this.nextId++,
        timestamp: Date.now(),
        category,
        action,
        payload,
      },
    ];
    this.entries =
      nextEntries.length > MAX_ENTRIES
        ? nextEntries.slice(nextEntries.length - MAX_ENTRIES)
        : nextEntries;
    this.notify();
  };

  /**
   * Subscribe to log updates. Immediately invoked once with the current entries.
   *
   * @param {DebugConsoleListener} listener - Called with the full entries list on every change
   * @returns {() => void} Unsubscribe function
   */
  subscribe = (listener: DebugConsoleListener): (() => void) => {
    this.listeners.add(listener);
    listener(this.entries);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Clear all logged entries.
   */
  clear = (): void => {
    this.entries = [];
    this.notify();
  };

  private notify(): void {
    this.listeners.forEach((listener) => listener(this.entries));
  }
}

const DebugConsoleInstance = DebugConsoleManager.getInstance();
export default DebugConsoleInstance;
export { DebugConsoleCategory };
export type { DebugConsoleEntry };
